import type { Attachment } from "@theta/api-client";
import { AudioPlayer } from "./AudioPlayer";
import { FileChip } from "./FileChip";
import { fileAttachments, readyAttachments } from "@/lib/attachments";

/**
 * The audio players and file chips that hang off a post, a comment or a
 * message — the third of the three renderers `attachments` needs, beside the
 * image gallery and `VideoPlayer`.
 *
 * WHY IT IS A COMPONENT AND NOT THREE COPIES. `MessageBubble` had these two
 * `.map`s inline and posts and comments had neither, which is exactly how an
 * MP3 came to work in beskeder and disappear from a group post. One component
 * is what stops the next surface being written without them — see
 * `fileAttachments` for what was actually lost.
 *
 * NOT FOLDED INTO `RichContent`, though it owns the body-derived audio and
 * files: those come out of the cooked html of a MIGRATED post and are found by
 * a strip pass, while these are rows the server sent. Keeping the two apart is
 * what lets a body that still contains its own `<a class="attachment">` render
 * it exactly once.
 *
 * Narrowed to `ready` rows: `status` is only ever anything else for a video
 * (`needsProcessing` is an extension test), so a non-ready audio or file would
 * be a row with no bytes and nothing to say about it.
 */
export function AttachedFiles({
  attachments,
  /** Margin above each row — the surface's own spacing, as in `RichContent`'s density. */
  gap = "mt-2",
}: {
  attachments: Attachment[];
  gap?: string;
}) {
  return (
    <>
      {readyAttachments(fileAttachments(attachments)).map((a) => (
        <div key={a.id} className={gap}>
          {a.kind === "audio" ? (
            <AudioPlayer src={a.url} filename={a.filename} />
          ) : (
            <FileChip name={a.filename} href={a.url} />
          )}
        </div>
      ))}
    </>
  );
}
